import { AxiosError } from 'axios';
import { Outlet, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useEffect, useState } from 'react';

import Header from '../Header';
import Footer from '../Footer';
import OngoingEvaluations from '@/features/ongoingEvaluations/components/OngoingEvaluationsPanel';

import { Iconlabel } from '@wk/components-react16';

import { getUserRoleAccess } from '@/domains/access/accessApi';
import { isNewsletterEnabledBySwagger } from '@/domains/newsletter/newsletterApi';
import useOnGoingEvaluationsStore from '@/features/ongoingEvaluations/stores/ongoingEvaluationsStore';
import useApplicationsStore from '@/domains/application/applicationStore';
import { APP_LAYOUT_MOBILE_ROUTE_LOADING_EVENT } from '@/shared/hooks';
import useToast from '@/shared/stores/toastStore';
import { SITE_COMPLIANCE_PORTAL_ENABLED } from '@/config/env';
import Toaster from '@/shared/ui/Toaster';
import type { ErrorResponse } from '@/types';

export type AppLayoutOutletContext = {
  isNewsletterAvailable: boolean;
};

const AppLayout = () => {
  const location = useLocation();
  const isApplicationsRoute = location.pathname.startsWith('/applications');
  const isEvaluationsRoute = location.pathname.startsWith('/evaluations');
  const isSiteComplianceRoute = SITE_COMPLIANCE_PORTAL_ENABLED;
  // O portal Site Compliance nunca consome as APIs do ORR (applications,
  // userRoleAccess, newsletter). Esses fetches ficam suprimidos sempre que o
  // portal SC esta ativo, independente de test mode / token injetado.
  const shouldBypassSiteComplianceTestAuth = SITE_COMPLIANCE_PORTAL_ENABLED;
  const isSettingsRoute = location.pathname.startsWith('/settings');
  const isNewsletterRoute = location.pathname.startsWith('/newsletter');
  const shouldUseMobileDocumentFlow =
    isApplicationsRoute ||
    isEvaluationsRoute ||
    isSiteComplianceRoute ||
    isSettingsRoute ||
    isNewsletterRoute;

  const { isLoading, error: applicationsError, fetchApplications } = useApplicationsStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      error: state.error,
      fetchApplications: state.fetchApplications,
    })),
  );

  useEffect(() => {
    if (shouldBypassSiteComplianceTestAuth) {
      return;
    }

    fetchApplications();
  }, [fetchApplications, shouldBypassSiteComplianceTestAuth]);

  const { isLoading: isUserRoleAccessLoading } = useQuery({
    enabled: !shouldBypassSiteComplianceTestAuth,
    queryKey: ['userRoleAccess'],
    queryFn: getUserRoleAccess,
  });
  const {
    isLoading: isNewsletterAvailabilityLoading,
    data: isNewsletterAvailable = false,
  } = useQuery({
    enabled: !shouldBypassSiteComplianceTestAuth,
    queryKey: ['newsletter', 'availability'],
    queryFn: isNewsletterEnabledBySwagger,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isInitialLayoutLoading =
    !shouldBypassSiteComplianceTestAuth &&
    (isLoading || isUserRoleAccessLoading || isNewsletterAvailabilityLoading);
  const [isRouteContentLoading, setIsRouteContentLoading] = useState(false);

  const { isOngoingEvaluationsOpen, setIsOngoingEvaluationsOpen } =
    useOnGoingEvaluationsStore(
      useShallow((state) => ({
        isOngoingEvaluationsOpen: state.isOngoingEvaluationsOpen,
        setIsOngoingEvaluationsOpen: state.setIsOngoingEvaluationsOpen,
      })),
    );
  const { clearToasts } = useToast(
    useShallow((state) => ({
      clearToasts: state.clearToasts,
    })),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleRouteLoading = (
      event: Event,
    ) => {
      const customEvent = event as CustomEvent<{ loading?: boolean }>;
      setIsRouteContentLoading(Boolean(customEvent.detail?.loading));
    };

    window.addEventListener(
      APP_LAYOUT_MOBILE_ROUTE_LOADING_EVENT,
      handleRouteLoading as EventListener,
    );

    return () => {
      window.removeEventListener(
        APP_LAYOUT_MOBILE_ROUTE_LOADING_EVENT,
        handleRouteLoading as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    const shouldAwaitRouteLoadingSignal = SITE_COMPLIANCE_PORTAL_ENABLED
      ? true
      : location.pathname === '/applications' ||
        (location.pathname.startsWith('/applications/') &&
          !location.pathname.startsWith('/applications/onboard')) ||
        location.pathname.startsWith('/evaluations');

    setIsRouteContentLoading(shouldAwaitRouteLoadingSignal);
  }, [location.pathname]);

  useEffect(() => {
    clearToasts();
  }, [clearToasts, location.pathname, location.search]);

  const isFooterSuppressedOnMobile =
    shouldUseMobileDocumentFlow &&
    (isInitialLayoutLoading || isRouteContentLoading);

  const renderContent = () => {
    if (applicationsError) {
      const appError = applicationsError as AxiosError<ErrorResponse>;
      return (
        <div className="w-full h-full justify-center items-center flex flex-col gap-2">
          <h2 className="text-lg font-bold">Error</h2>
          <p className="text-sm text-gray-500">
            {appError?.response?.data.title ||
              'An error occurred while fetching application data.'}
          </p>
        </div>
      );
    }

    if (isInitialLayoutLoading) {
      return (
        <div className="flex w-full flex-1 min-h-[55vh] items-center justify-center">
          <span className="animate-spin">
            <Iconlabel name="spinner-alt" />
          </span>
        </div>
      );
    }
    return <Outlet context={{ isNewsletterAvailable }} />;
  };

  return (
    <div
      className={`h-full w-full flex flex-col relative ${
        shouldUseMobileDocumentFlow
          ? 'max-sm:h-auto max-sm:min-h-dvh max-sm:overflow-x-hidden'
          : 'max-sm:overflow-x-hidden'
      }`}
    >
      <Header />
      <div
        className="flex flex-col gap-4 px-4 lg:pr-8 py-4 w-full min-h-0 flex-1"
      >
        {renderContent()}
        <OngoingEvaluations
          isOpen={isOngoingEvaluationsOpen}
          onClose={() => setIsOngoingEvaluationsOpen(false)}
        />
        <Toaster />
      </div>
      <div
        className={
          isFooterSuppressedOnMobile ? 'max-sm:hidden' : ''
        }
      >
        <Footer isNewsletterAvailable={isNewsletterAvailable} />
      </div>
    </div>
  );
};

export default AppLayout;
